import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Image,
  Card,
  Button,
  Badge,
} from 'react-bootstrap';
import { CourseItemProps } from '../../../types/course';
// Import immagini
import beSmartImage from '../../../assets/images/rimettiti-al-centro/be-smart.jpg';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoginModal from '../loginModal/LoginModal';
import ContactCourseModal from '../contactCourseModal/ContactCourseModal';

const CourseItem: React.FC<CourseItemProps> = ({
  course,
  index,
  isExpanded = false,
  onToggle = () => {},
  handleDownloadRequest,
}) => {
  const cardRef = React.useRef<HTMLDivElement>(null);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  // Determina il layout in base al corso
  const isBeSmart = course.id === 'be-smart';
  const isComingSoon = course.comingSoon || false;
  const isForWomenOnly = course.forWomenOnly || false;

  const handleHeaderClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const wasExpanded = isExpanded;

    onToggle();

    if (!wasExpanded) {
      // Si sta aprendo: ricalcola la posizione DOPO il re-render
      setTimeout(() => {
        if (cardRef.current) {
          const rect = cardRef.current.getBoundingClientRect();
          const scrollTop = window.scrollY + rect.top - 100;
          window.scrollTo({ top: scrollTop, behavior: 'smooth' });
        }
      }, 50);
    }
  };

  // Se loggato → vai diretto a Fit Anatomy dashboard
  // Se non loggato → apri LoginModal con tab accesso gratuito
  const handleDownloadRequestLogin = () => {
    if (isAuthenticated) {
      navigate('/dashboard/fit-anatomy');
    } else {
      setShowModal(true);
    }
  };
  // Renderizza il contenuto specifico per BE SMART
  const renderBeSmartContent = () => {
    if (!isExpanded) return null;

    return (
      <>
        {/* Layout per BE SMART: immagine a sinistra, testo a destra */}
        <Row className="align-items-start mb-5">
          {/* Colonna sinistra - Immagine (piccola su desktop) */}
          <Col lg={4} className="mb-4 mb-lg-0">
            <div className="text-center">
              <Image
                src={beSmartImage}
                alt={`${course.title} - ${course.subtitle}`}
                fluid
                rounded
                className="be-smart-image"
                style={{
                  border: '6px solid white',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                  maxWidth: '350px',
                }}
              />
            </div>
          </Col>

          {/* Colonna destra - Testo */}
          <Col lg={8}>
            <div className="course-details">
              <h2 className="section-title fw-bold mb-4">
                {course.title} è il manuale che ho preparato per chi studia o
                lavora "in smart" che salva la schiena, un sacco di soldi e
                anche il futuro!
              </h2>

              <div className="mb-4">
                <p className="mb-3">
                  Perché, parliamoci chiaro: quando la schiena fa male non si
                  riesce a fare più nulla in modo spensierato e con la stessa
                  energia di prima, diventa tutto estremamente faticoso,
                  doloroso e i nervi saltano subito.
                </p>
                <p className="mb-4">
                  Di questi tempi anche i giovanissimi trascorrono già molte ore
                  piegati sui dispositivi, anticipando di decenni dolori
                  derivanti da posture distorte. Ecco perché questo manuale può
                  essere l'assicurazione per il tuo futuro!
                </p>
              </div>

              <div className="mb-4">
                <p className="fw-bold mb-3">
                  Non è solo un manuale che puoi leggere in pdf, {course.title}{' '}
                  è una collezione di:
                </p>
                <Row className="g-0">
                  <Col md={6}>
                    <ul className="list-unstyled mb-md-0">
                      <li className="mb-2">
                        • Oltre 60 preziosissimi suggerimenti
                      </li>
                      <li className="mb-2">• 27 esercizi pratici</li>
                      <li className="mb-2">• Spiegati con parole semplici</li>
                    </ul>
                  </Col>
                  <Col md={6}>
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">• 15 video esplicativi</li>
                      <li className="mb-2">• 3 tracce audio guidate</li>
                      <li className="mb-2">• Consigli PRIMA e POI</li>
                    </ul>
                  </Col>
                </Row>
              </div>

              <p className="mb-4">
                Consigli preziosi da mettere in pratica <strong>PRIMA</strong>,
                per evitare di fare danni, ma anche suggerimenti e istruzioni
                per trovare sollievo <strong>POI</strong> in tanti modi diversi
                e non solo per la schiena, ma anche per il collo, per gli occhi,
                per le spalle, per le gambe, perfino per il pavimento pelvico!
              </p>
            </div>
          </Col>
        </Row>

        {/* Sezione esclusiva BE SMART */}
        {course.exclusiveContent && (
          <Row className="mb-5">
            <Col>
              <Card
                className="border-primary border-2"
                style={{ backgroundColor: '#f8f9fa' }}
              >
                <Card.Body className="p-4">
                  <h4 className="fw-bold text-primary mb-3">
                    <Badge bg="primary" className="me-2">
                      ESCLUSIVO!
                    </Badge>
                    {course.exclusiveContent}
                  </h4>
                  <p className="mb-0">
                    ... cosa stai ancora aspettando?{' '}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={course.ctaButtons?.primary?.action || (() => {})}
                      className="ms-2"
                    >
                      🛒 AGGIUNGI AL CARRELLO
                    </Button>
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Sezione CTA e risorse BE SMART */}
        <Row className="align-items-start">
          <Col lg={6} className="mb-4 mb-lg-0">
            <h3 className="fw-bold mb-4">
              Inizia subito a VIVERE MEGLIO: VIVI A TESTA ALTA{' '}
            </h3>

            <div className="free-resources-section mb-4">
              <h5 className="fw-bold mb-3">e accedi a:</h5>
              <div className="resources-grid">
                <div className="download-options mb-4">
                  <Button
                    variant="outline-secondary"
                    className="me-2 mb-2"
                    onClick={handleDownloadRequestLogin}
                  >
                    Scarica il pdf del Capitolo 1
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className="me-2 mb-2"
                    onClick={handleDownloadRequestLogin}
                  >
                    Guarda subito il Video n. 1
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className="me-2 mb-2"
                    onClick={handleDownloadRequestLogin}
                  >
                    Guarda subito il Video n. 2
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className="me-2 mb-2"
                    onClick={handleDownloadRequestLogin}
                  >
                    Ascolta la traccia n. 1
                  </Button>
                </div>
                {/*{course.freeResources?.map((resource, idx) => (
                  <Button
                    key={idx}
                    variant="outline-secondary"
                    className="text-start mb-2 resource-button"
                    onClick={handleDownloadRequest}
                    style={{
                      display: 'block',
                      width: '100%',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      padding: '0.75rem',
                      backgroundColor: 'white',
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <span
                        className="resource-icon me-3"
                        style={{ fontSize: '1.2rem' }}
                      >
                        {resource.icon}
                      </span>
                      <span className="fw-medium">{resource.title}</span>
                    </div>
                  </Button>
                ))}*/}
              </div>
            </div>
          </Col>

          {/* Colonna destra - Prezzo e acquisto BE SMART */}
          <Col lg={6}>
            <Card
              className="border-0 shadow-sm"
              style={{ backgroundColor: 'white' }}
            >
              <Card.Body className="p-4">
                <div className="price-section mb-4 text-center">
                  <h2
                    className="fw-bold text-primary mb-2"
                    style={{ fontSize: '2rem' }}
                  >
                    {course.title} A SOLI {course.price}
                  </h2>
                  {course.discountNote && (
                    <p
                      className="text-muted mb-4"
                      style={{ fontSize: '0.9rem' }}
                    >
                      *{course.discountNote}
                    </p>
                  )}
                </div>

                <div className="actions-section">
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={course.ctaButtons?.primary?.action || (() => {})}
                    className="mb-3 w-100 py-3"
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                    }}
                  >
                    🛒 AGGIUNGI AL CARRELLO
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  // Renderizza il contenuto per tutti gli altri corsi (stile BE XPERT)
  const renderStandardContent = () => {
    if (!isExpanded) return null;

    return (
      <>
        {/* Badge COMING SOON */}
        {isComingSoon && (
          <Row className="mb-4">
            <Col className="text-center">
              <Badge bg="warning" className="coming-soon-badge px-4 py-2 mb-4">
                <span className="h4 fw-bold mb-0">COMING SOON …</span>
              </Badge>
            </Col>
          </Row>
        )}

        {/* Badge FOR WOMEN ONLY */}
        {isForWomenOnly && (
          <Row className="mb-3">
            <Col className="text-center">
              <Badge bg="pink" className="women-only-badge px-4 py-2 mb-2">
                <span className="h5 fw-bold mb-0">FOR WOMEN ONLY</span>
              </Badge>
            </Col>
          </Row>
        )}

        {/* Contenuto principale */}
        <Row className="mb-5">
          <Col>
            <div className="course-description-main">
              {course.description && (
                <div className="mb-5">
                  <p className="lead" style={{ fontSize: '1.1rem' }}>
                    {course.description}
                  </p>
                </div>
              )}

              {/* Contenuto aggiuntivo specifico */}
              {course.additionalContent && (
                <div className="mb-5">
                  <div
                    className="additional-content"
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.closest('.additional-content-cta-link')) {
                        e.preventDefault();
                        handleDownloadRequestLogin();
                      }
                    }}
                  >
                    {course.additionalContent}
                  </div>
                </div>
              )}

              {/* CTA finale per tutti i corsi standard */}
              <div className="final-cta-standard text-center">
                {course.finalCta && (
                  <h3 className="fw-bold mb-3">{course.finalCta}</h3>
                )}

                {course.finalHighlight && (
                  <h2 className="fw-bold text-primary mb-4">
                    {course.finalHighlight}
                  </h2>
                )}

                {/* Pulsante in base allo stato del corso */}
                {course.workInProgress ? (
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() =>
                      (window.location.href = '/contatti?interest=' + course.id)
                    }
                    className="px-5 py-3"
                    style={{ fontSize: '1.1rem', fontWeight: '600' }}
                    disabled
                  >
                    🛠️ WORK IN PROGRESS
                  </Button>
                ) : isComingSoon ? (
                  <Button
                    variant="warning"
                    size="lg"
                    onClick={() => setShowContactModal(true)}
                    className="px-5 py-3"
                    style={{ fontSize: '1.1rem', fontWeight: '600' }}
                  >
                    📩 TIENIMI INFORMATO SU QUESTO CORSO
                  </Button>
                ) : (
                  course.ctaButtons?.primary && (
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={course.ctaButtons.primary.action}
                      className="px-5 py-3"
                      style={{ fontSize: '1.1rem', fontWeight: '600' }}
                    >
                      🛒 {course.ctaButtons.primary.text}
                    </Button>
                  )
                )}
              </div>
            </div>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <section
      className={`content-section py-5 ${index % 2 === 0 ? 'bg-light' : ''} ${!isBeSmart ? 'course-without-image' : ''}`}
      id={course.id}
    >
      <Container>
        {/* Box clickable superiore */}
        <Row className="mb-4">
          <Col>
            <Card
              ref={cardRef}
              className={`clickable-header-card border-0 shadow-sm bg-dark text-white final-section ${isExpanded ? 'active' : ''}`}
              onClick={handleHeaderClick}
              style={{
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            >
              <Card.Body className="text-center p-4">
                <h3 className="fw-bold mb-0" style={{ fontSize: '1.5rem' }}>
                  {course.title}
                  <span
                    className="text-secondary ms-2"
                    style={{ fontSize: '1.2rem' }}
                  >
                    Clicca qui
                  </span>
                </h3>
                {course.subtitle && (
                  <p className="mb-0 mt-2 text-primary">{course.subtitle}</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Contenuto espandibile */}
        <div
          className={`course-content-wrapper ${isExpanded ? 'expanded' : 'collapsed'}`}
        >
          {isBeSmart ? renderBeSmartContent() : renderStandardContent()}
        </div>
      </Container>
      <ContactCourseModal
        show={showContactModal}
        onHide={() => setShowContactModal(false)}
        courseTitle={course.title}
      />
      {/* LoginModal con tab accesso gratuito, condiviso da tutte le risorse free del corso */}
      <LoginModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onLoginSuccess={() => {
          setShowModal(false);
          navigate('/dashboard/fit-anatomy');
        }}
        showFreeAccessTab
        disableRedirect
      />
    </section>
  );
};

export default CourseItem;
